import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { RoleService } from './role.service';

@Injectable({ providedIn: 'root' })
export class RoleStateService {
  private rolesSubject = new BehaviorSubject<any[] | null>(null);
  readonly roles$ = this.rolesSubject.asObservable();

  constructor(private roleService: RoleService) { }

  get roles(): any[] {
    return this.rolesSubject.value ?? [];
  }

  get isRolesLoaded(): boolean {
    return this.rolesSubject.value !== null;
  }

  readonly rolesLoaded$ = this.roles$.pipe(
    filter((roles): roles is any[] => roles !== null),
    take(1)
  );

  setRoles(roles: any[]): void {
    this.rolesSubject.next(roles);
  }

  clear(): void {
    this.rolesSubject.next(null);
  }

  loadRoles(userId: string): Observable<boolean> {
    return this.roleService.sideModules(userId).pipe(
      map((data: any[]) => {
        const menuData = data.reduce((acc: any[], item: any) => {
          const moduleName = item.moduleName;
          const functionName = item.functionName;
          let module = acc.find((m: any) => m.moduleName === moduleName);
          if (!module) {
            module = { moduleName, functions: [], routing: item.moduleUrl, displayOrder: item.moduleDisplayNo };
            acc.push(module);
          }
          let func = module.functions.find((f: any) => f.functionName === functionName);
          if (!func) {
            func = { functionName, routing: item.functionUrl, featureName: [], displayOrder: item.functionDisplayNo };
            module.functions.push(func);
          }
          if (item.featureName && !func.featureName.includes(item.featureName)) {
            func.featureName.push(item.featureName);
          }
          return acc;
        }, []);

        const processed = menuData.map((item: any) => ({
          moduleName: item.moduleName,
          moduleUrl: item.routing,
          functions: item.functions.map((func: any) => ({
            functionName: func.functionName,
            functionUrl: func.routing,
            featureName: func.featureName
          }))
        }));

        this.setRoles(processed);
        return true;
      }),
      catchError(() => {
        this.setRoles([]);
        return of(true);
      })
    );
  }

  hasModule(url: string): boolean {
    return this.roles.some(mod => url.includes(mod.moduleUrl));
  }

  hasFunction(module: string, func: string): boolean {
    return this.roles.some(role =>
      role.moduleUrl === module &&
      role.functions?.some((f: any) => f.functionUrl?.includes(func))
    );
  }

  hasFeature(feature: string): boolean {
    return this.roles.some(mod =>
      mod.functions?.some((fn: any) => fn.featureName?.includes(feature))
    );
  }
}